import React, { useState } from 'react'
import { colors, transition, UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SM, UI_TITLE_SM, WrapperCardGrid } from 'oolib'
import styled from 'styled-components'
import { ButtonPrimary } from 'oolib/dist/v2/components/Buttons'
import { TextInput } from 'oolib/dist/v2/components/TextInputs'

const StyledCardRadioWrapper = styled.div`
  padding: 12px;
  border: 1px solid ${colors.greyColor10};
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 8px;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.04);
  ${transition('border-color', 'box-shadow')}
  cursor: pointer;

  &:hover {
    border-color: ${colors.greyColor50};
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.08);
  }

  &.-selected- {
    border-color: ${({theme}) => theme.colors.primaryColor100};
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.08);
  }
`

// ─── Helpers ───

const isSelected = (option, value, saveValueAsString) => {
  if (!value || !Array.isArray(value)) return false
  if (saveValueAsString) {
    return value.includes(option.display_desc ?? option.display ?? option.value)
  }
  return value.some(v => v.value === option.value)
}

const toggleOption = ({ option, value, multiSelect, saveValueAsString, onChange, id }) => {
  const current = Array.isArray(value) ? value : []
  const stringVal = option.display_desc ?? option.display ?? option.value

  if (saveValueAsString) {
    if (multiSelect) {
      const next = current.includes(stringVal)
        ? current.filter(v => v !== stringVal)
        : [...current, stringVal]
      onChange(id, next)
    } else {
      onChange(id, [stringVal])
    }
  } else {
    const optObj = { value: option.value, display: option.display || option.title, ...(option.desc && { desc: option.desc }) }
    if (multiSelect) {
      const next = current.some(v => v.value === option.value)
        ? current.filter(v => v.value !== option.value)
        : [...current, optObj]
      onChange(id, next)
    } else {
      onChange(id, [optObj])
    }
  }
}

// ─── CardRadioInput ───

const CardRadioInput = (props) => {
  const {
    value,
    options = [],
    onChange,
    id,
    style,
    multiSelect = false,
    saveValueAsString = false,
    injectOtherOption = false,
  } = props

  const [otherText, setOtherText] = useState('')

  const otherOption = injectOtherOption
    ? { value: 'other', display: 'Other' }
    : null

  const optionValues = options.map(o => o.display_desc ?? o.display ?? o.value)

  // For saveValueAsString: "other" is selected if any value doesn't match a known option
  // For object mode: check for value === 'other'
  const otherIsSelected = (() => {
    if (!injectOtherOption || !Array.isArray(value) || value.length === 0) return false
    if (saveValueAsString) {
      return value.some(v => !optionValues.includes(v))
    }
    return value.some(v => v.value === 'other')
  })()

  const handleOtherTextChange = (_, text) => {
    setOtherText(text)
    const current = Array.isArray(value) ? value : []
    if (saveValueAsString) {
      const withoutOther = current.filter(v => optionValues.includes(v))
      onChange(id, [...withoutOther, text || 'Other'])
    } else {
      const withoutOther = current.filter(v => v.value !== 'other')
      onChange(id, [...withoutOther, { value: 'other', display: 'Other', display_desc: text }])
    }
  }

  return (
    <div>
      <WrapperCardGrid style={style} id={id}>
        {options.map((d) => {
          const display = d.display || d.title
          const desc = d.desc || d.subtitle
          return (
            <CardRadio
              key={d.value}
              selected={isSelected(d, value, saveValueAsString)}
              display={display}
              desc={desc}
              action={d.action}
              onClick={() => toggleOption({ option: d, value, multiSelect, saveValueAsString, onChange, id })}
            />
          )
        })}
        {otherOption && (
          <CardRadio
            key="other"
            selected={otherIsSelected}
            display="Other"
            onClick={() => toggleOption({ option: otherOption, value, multiSelect, saveValueAsString, onChange, id })}
          />
        )}
      </WrapperCardGrid>
      {otherOption && otherIsSelected && (
        <div style={{ marginTop: '12px' }}>
          <TextInput
            placeholder="Please specify..."
            value={otherText}
            onChange={handleOtherTextChange}
          />
        </div>
      )}
    </div>
  )
}

// ─── CardRadio (single card) ───

export const CardRadio = (props) => {
  const { display, desc, action, selected, onClick } = props

  return (
    <StyledCardRadioWrapper
      onClick={onClick}
      className={`${selected ? '-selected-' : ''}`}>
      <div>
        {display && <UI_TITLE_SM>{display}</UI_TITLE_SM>}
        {desc && <UI_BODY_SM color="greyColor50" style={{ marginTop: '0.4rem' }}>{desc}</UI_BODY_SM>}
      </div>
      {action && (
        <div style={{ margin: '0 auto' }}>
          <ButtonPrimary
            style={{ marginTop: '1.5rem' }}
            children={action.text}
          />
        </div>
      )}
    </StyledCardRadioWrapper>
  )
}

export default CardRadioInput
